import React, { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useApolloClient, useMutation } from '@apollo/client'
import { GET_ORDER } from '../graphql/queries'
import { GET_DELIVERY_RUNS, GET_ORDER_DELIVERY_RUN } from '../graphql/delivery-run'
import { formatTime as formatClockTime } from '../lib/format'
import { RECORD_EXTERNAL_REFUND, REFUND_ORDER_FULL, REFUND_ORDER_PARTIAL } from '../graphql/mutations'
import { OrderService } from '../lib/orderService'
import { ReceiptService } from '../lib/receiptService'
import { PaymentService } from '../lib/paymentService'
import { ReceiptUtils } from '../lib/receiptUtils'
import { DeliveryRun, DeliveryRunDetail, Order, OrderStatus, OrderLine, Payment, Receipt } from '../types/order'
import LoadingSpinner from '../components/LoadingSpinner'
import ReceiptModal from '../components/ReceiptModal'
import RefundModal from '../components/RefundModal'
import ExternalRefundModal, {
  externalRefundTotals,
  isExternallyRecordedRefund,
  refundProviderLabel,
  refundedWhere,
} from '../components/ExternalRefundModal'
import HandleExternallyModal from '../components/HandleExternallyModal'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import {
  canPrintDeliverySlip,
  countedStops,
  formatEta,
  isOwnDelivery,
  openDeliverySlips,
  stopNumbers,
} from '../lib/delivery'
import { useTerminal } from '../contexts/TerminalContext'
import toast from 'react-hot-toast'
import { 
  ArrowLeftIcon,
  DocumentTextIcon,
  CheckCircleIcon, 
  ClockIcon, 
  ExclamationTriangleIcon,
  XMarkIcon,
  CurrencyEuroIcon,
  UserIcon,
  ShoppingBagIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'

// Exhaustive by type: adding an OrderStatus without an entry here is a compile
// error. These replace switch-with-default helpers that silently rendered any
// new status gray with a clock icon.
const STATUS_COLORS: Record<OrderStatus, string> = {
  UNPAID: 'bg-yellow-100 text-yellow-800',
  RECEIVED: 'bg-orange-100 text-orange-800',
  SCHEDULED: 'bg-cyan-100 text-cyan-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  PREPARING: 'bg-orange-100 text-orange-800',
  READY: 'bg-green-100 text-green-800',
  IN_DELIVERY: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-red-100 text-red-800',
  REFUNDED: 'bg-purple-100 text-purple-800',
}

const STATUS_ICONS: Record<OrderStatus, React.ComponentType<{ className?: string }>> = {
  UNPAID: ClockIcon,
  RECEIVED: ExclamationTriangleIcon,
  SCHEDULED: ClockIcon,
  CONFIRMED: CheckCircleIcon,
  PREPARING: ExclamationTriangleIcon,
  READY: CheckCircleIcon,
  IN_DELIVERY: ArrowPathIcon,
  COMPLETED: CheckCircleIcon,
  CANCELLED: XMarkIcon,
  REFUNDED: ArrowPathIcon,
}

const OrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const apolloClient = useApolloClient()
  const orderService = new OrderService(apolloClient)
  const receiptService = new ReceiptService(apolloClient)
  const paymentService = new PaymentService(apolloClient)
  const { canStartPreparingOrders, canCancelOrders, canProcessRefund, canRecordExternalRefund, canViewMargins, hasAnyPermission, hasPermission } = usePermissions()
  const { selectedTerminal } = useTerminal()
  const canHandleIncoming = hasAnyPermission([PERMISSIONS.ORDER_CHECKOUT, PERMISSIONS.PAYMENT_CASH_CREATE])
  const [isHandleExternallyOpen, setIsHandleExternallyOpen] = useState(false)

  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isReceiptOpen, setIsReceiptOpen] = useState(false)
  const [isCreatingReceipt, setIsCreatingReceipt] = useState(false)
  const [isReceiptMenuOpen, setIsReceiptMenuOpen] = useState(false)
  const [receiptType, setReceiptType] = useState<'CUSTOMER' | 'MERCHANT'>('CUSTOMER')
  const [isRefundReceiptOpen, setIsRefundReceiptOpen] = useState(false)
  const [refundReceiptPaymentId, setRefundReceiptPaymentId] = useState<number | null>(null)
  // Refunds are always customer-only, so this should never be MERCHANT
  const [refundReceiptType, setRefundReceiptType] = useState<'CUSTOMER'>('CUSTOMER')
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false)
  const [isProcessingRefund, setIsProcessingRefund] = useState(false)
  const [isRefundVerifying, setIsRefundVerifying] = useState(false)
  const [refundPollingAttempts, setRefundPollingAttempts] = useState(0)
  const [refundPollingInterval, setRefundPollingInterval] = useState<number | null>(null)
  const [refundPollingError, setRefundPollingError] = useState<string | null>(null)
  const [activeRefundPayment, setActiveRefundPayment] = useState<Payment | null>(null)
  const [isCancellingRefund, setIsCancellingRefund] = useState(false)
  const [refundMode, setRefundMode] = useState<'PARTIAL' | 'FULL'>('PARTIAL')
  const [refundFixedAmountCents, setRefundFixedAmountCents] = useState<number | null>(null)
  // Refund action flags completion tracking
  const [completedRefundActions, setCompletedRefundActions] = useState<Set<string>>(new Set())
  const [refundReceipt, setRefundReceipt] = useState<Receipt | null>(null)
  // Webshop / platform orders: the refund happened at Mollie or the platform and is only recorded.
  const [isExternalRefundOpen, setIsExternalRefundOpen] = useState(false)
  const [isRecordingExternalRefund, setIsRecordingExternalRefund] = useState(false)

  // No refetchQueries: the page's GET_ORDER runs `no-cache`, so the handler calls refetch() itself.
  const [recordExternalRefund] = useMutation(RECORD_EXTERNAL_REFUND)

  const [refundOrderFull] = useMutation(REFUND_ORDER_FULL, {
    refetchQueries: [{ query: GET_ORDER, variables: { id: parseInt(id!) } }],
    awaitRefetchQueries: true
  })

  const [refundOrderPartial] = useMutation(REFUND_ORDER_PARTIAL, {
    refetchQueries: [{ query: GET_ORDER, variables: { id: parseInt(id!) } }],
    awaitRefetchQueries: true
  })

  const { data, loading, error: queryError, refetch } = useQuery(GET_ORDER, {
    variables: { id: parseInt(id!) },
    skip: !id,
    fetchPolicy: 'no-cache'
  })

  const order: Order | null = data?.order || null

  // Route facts for an own delivery (pos-delivery-run-contract.md → order detail page): the
  // same fields the customer's tracking page shows. A separate query, NOT fields on
  // GetOrder, so this page keeps loading against an API that has no routes yet.
  const ownDeliveryOrder = Boolean(order && isOwnDelivery(order))
  const {
    data: routeData,
    refetch: refetchRoute,
    startPolling: startRoutePolling,
    stopPolling: stopRoutePolling,
  } = useQuery(GET_ORDER_DELIVERY_RUN, {
    variables: { id: parseInt(id!) },
    skip: !id || !ownDeliveryOrder,
    fetchPolicy: 'cache-and-network',
  })
  const routeInfo = (routeData?.order ?? null) as Pick<
    Order,
    'delivery_run' | 'delivery_run_position' | 'delivery_eta_at' | 'delivery_stops_before'
  > | null
  const routeRun: DeliveryRun | null = routeInfo?.delivery_run ?? null

  // "Stop n of N" must count like stops_total, and the raw delivery_run_position does not
  // once a stop is cancelled on an ACTIVE route (lib/delivery.ts stopNumbers). This page's
  // own query carries no stop list, so: count from the route's stops when this user may
  // see routes (delivery.plan — front of house; last 24h of completed routes included);
  // otherwise trust the raw position only on a PLANNED route, which the API renumbers on
  // every change. Anything else shows no stop number rather than "Stop 3 of 2".
  const canSeeRoutes = hasPermission(PERMISSIONS.DELIVERY_PLAN)
  const {
    data: routesData,
    startPolling: startRoutesPolling,
    stopPolling: stopRoutesPolling,
  } = useQuery(GET_DELIVERY_RUNS, {
    variables: { includeCompleted: true },
    skip: !routeRun || !canSeeRoutes,
    fetchPolicy: 'cache-and-network',
  })
  const routeStops = ((routesData?.deliveryRuns ?? []) as DeliveryRunDetail[]).find(
    run => run.id === routeRun?.id
  )?.stops
  // Number and total always come from ONE snapshot. The total used to be stops_total from
  // the order's route query (polled) while the number was counted from this stop list
  // (loaded once) — two queries on two clocks, so a stop cancelled while the page was open
  // read "Stop 3 of 2" until a reload. Counted from the same list they can only lag
  // together, never contradict; the PLANNED fallback pairs position with stops_total, both
  // from the order's own query.
  let routeStop: { number: number; total: number } | undefined
  if (order && routeStops) {
    const number = stopNumbers(routeStops).get(order.id)
    if (number !== undefined) routeStop = { number, total: countedStops(routeStops).length }
  } else if (routeRun?.status === 'PLANNED' && routeInfo?.delivery_run_position != null) {
    routeStop = { number: routeInfo.delivery_run_position, total: routeRun.stops_total }
  }

  // Poll only while the route is on the road: that is when the arrival estimate moves (after
  // every delivered stop) and a stop can drop out. A planned or finished route does not
  // change on its own. The stop list polls alongside so the stop number keeps up — only
  // when that query runs at all; without delivery.plan it is skipped, with nothing to poll.
  const routeActive = routeRun?.status === 'ACTIVE'
  useEffect(() => {
    if (routeActive) startRoutePolling(30_000)
    else stopRoutePolling()
    if (routeActive && canSeeRoutes) startRoutesPolling(30_000)
    else stopRoutesPolling()
    return () => {
      stopRoutePolling()
      stopRoutesPolling()
    }
  }, [routeActive, canSeeRoutes, startRoutePolling, stopRoutePolling, startRoutesPolling, stopRoutesPolling])

  // GetOrder is no-cache and refetched after a status change made HERE (e.g. the FOH
  // "Mark delivered" fallback, which can complete the route) — follow it with the route.
  // Only on a real change, not on first load, where the route query is already running.
  const lastOrderStatusRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const status = order?.status
    if (lastOrderStatusRef.current !== undefined && status !== lastOrderStatusRef.current && ownDeliveryOrder) {
      refetchRoute().catch(() => undefined)
    }
    lastOrderStatusRef.current = status
  }, [order?.status, ownDeliveryOrder, refetchRoute])

  // Cleanup refund polling on unmount
  useEffect(() => {
    return () => {
      if (refundPollingInterval) {
        clearInterval(refundPollingInterval)
      }
    }
  }, [refundPollingInterval])

  const handleStatusUpdate = async (newStatus: OrderStatus) => {
    setIsUpdating(true)
    setError(null)
    
    try {
      await orderService.updateOrderStatus(Number(id), newStatus)
      await refetch()
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update order status')
    } finally {
      setIsUpdating(false)
    }
  }

  const handleCreateReceipt = async (type: 'CUSTOMER' | 'MERCHANT') => {
    if (!order) return
    
    setIsCreatingReceipt(true)
    setError(null)
    
    try {
      // Attempt to fetch/create the receipt
      setReceiptType(type)
      await receiptService.getReceipt(order.id, type)
      // If successful, open the receipt modal
      setIsReceiptOpen(true)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create receipt. Please try again.')
    } finally {
      setIsCreatingReceipt(false)
    }
  }

  // Build payment_config for card refunds (matches API structure)
  const buildPaymentConfig = (payment: Payment | undefined) => {
    // Only include payment_config for card payments
    if (!payment || payment.payment_method !== 'CARD') {
      return undefined
    }

    // Use terminal_id from original payment if available, otherwise use selected terminal or fallback
    const terminalId = payment.terminal_id || selectedTerminal || 'LEEN0208'
    
    // If terminal is explicitly set to empty string (None), we can't process card refund
    if (selectedTerminal === '' || (!payment.terminal_id && selectedTerminal === '')) {
      throw new Error('Payment terminal is required for card refunds. Please select a terminal and try again.')
    }

    return {
      terminal_id: terminalId,
      management_system_id: '000103030',
      access_protocol: payment.access_protocol || 'OPI_NL',
      operating_environment: payment.operating_environment || 'ATTENDED',
      language: 'nld'
    }
  }

  // Start polling refund payment status (for card refunds)
  const startRefundPolling = (paymentId: number) => {
    const maxAttempts = 360 // up to 360 seconds
    const pollIntervalMs = 1000
    let attempts = 0

    // Ensure processing screen shows first by clearing activeRefundPayment and setting verifying state
    setActiveRefundPayment(null)
    setIsRefundVerifying(true)
    setRefundPollingAttempts(0)
    setRefundPollingError(null)
    setCompletedRefundActions(new Set())

    let intervalId: number

    const poll = async () => {
      try {
        const updatedPayment = await paymentService.getPaymentStatus(paymentId)
        attempts++
        setRefundPollingAttempts(attempts)

        if (updatedPayment.status === 'COMPLETED' || updatedPayment.status === 'REFUNDED') {
          // Check if there are required actions
          const hasActions = 
            updatedPayment.ask_merchant_signature === true ||
            updatedPayment.ask_customer_signature === true ||
            updatedPayment.ask_customer_identification === true ||
            updatedPayment.print_customer_receipt === true

          if (hasActions) {
            // Keep payment active
            setActiveRefundPayment(updatedPayment)

            // Refunds are customer-only. Load the customer receipt (needed for print action and helpful for other compliance actions).
            try {
              const receipt = await receiptService.getCustomerReceiptByPayment(paymentId)
              setRefundReceipt(receipt)
            } catch (err) {
              // Continue without receipt - user can still mark actions complete
            }

            // Stop polling but keep verifying state active
            clearInterval(intervalId)
            setRefundPollingInterval(null)
            setIsRefundVerifying(false)
            toast.success('Refund completed. Please complete required actions.')
            // Refetch order data to update order status
            await refetch()
          } else {
            // No actions required, close verification and show receipt
            clearInterval(intervalId)
            setRefundPollingInterval(null)
            setIsRefundVerifying(false)
            toast.success('Refund completed successfully')
            setRefundReceiptPaymentId(paymentId)
            setIsRefundReceiptOpen(true)
            setActiveRefundPayment(null)
            await refetch()
          }
        } else if (updatedPayment.status === 'FAILED' || updatedPayment.status === 'CANCELED') {
          clearInterval(intervalId)
          setRefundPollingInterval(null)
          setIsRefundVerifying(false)
          const msg = 'Refund failed or was cancelled. Please check the terminal.'
          setRefundPollingError(msg)
          toast.error(msg)
          setActiveRefundPayment(null)
          // Refetch order data to ensure status is up to date
          await refetch()
        } else if (attempts >= maxAttempts) {
          clearInterval(intervalId)
          setRefundPollingInterval(null)
          setIsRefundVerifying(false)
          const msg = 'Refund verification timed out. Please verify the refund status manually.'
          setRefundPollingError(msg)
          toast.error(msg)
        }
      } catch (err) {
        attempts++
        setRefundPollingAttempts(attempts)
        if (attempts >= maxAttempts) {
          clearInterval(intervalId)
          setRefundPollingInterval(null)
          setIsRefundVerifying(false)
          const msg = 'Failed to verify refund status. Please try again or check the terminal.'
          setRefundPollingError(msg)
          toast.error(msg)
        }
      }
    }

    // Start polling - first poll will likely return PENDING, showing processing screen
    intervalId = window.setInterval(poll, pollIntervalMs)
    setRefundPollingInterval(intervalId)
    // Run first poll immediately
    poll()
  }

  const handleCancelRefund = async () => {
    setIsCancellingRefund(true)

    try {
      // Stop polling if active
      if (refundPollingInterval) {
        clearInterval(refundPollingInterval)
        setRefundPollingInterval(null)
      }

      // Attempt to cancel on terminal (if terminal selected)
      await paymentService.cancelPayment(selectedTerminal)

      setIsRefundVerifying(false)
      setRefundPollingAttempts(0)
      setRefundPollingError('Refund was cancelled on the terminal.')
      toast.success('Refund cancelled on terminal')
    } catch (err) {
      const msg = 'Failed to cancel refund. Please check the terminal.'
      setRefundPollingError(msg)
      toast.error(msg)
    } finally {
      setIsCancellingRefund(false)
      setActiveRefundPayment(null)
    }
  }

  // Handler functions for refund action flags
  const handleRefundActionComplete = (actionType: string) => {
    setCompletedRefundActions(prev => new Set(prev).add(actionType))
  }

  const handleRefundMerchantSignatureComplete = () => {
    handleRefundActionComplete('merchant_signature')
  }

  const handleRefundCustomerSignatureComplete = () => {
    handleRefundActionComplete('customer_signature')
  }

  const handleRefundCustomerIDComplete = () => {
    handleRefundActionComplete('customer_identification')
  }

  const handleRefundReceiptPrintComplete = () => {
    if (refundReceipt) {
      // Refund receipts use the dedicated refund endpoint
      if (refundReceipt.receipt_id?.startsWith('RFD-')) {
        ReceiptUtils.printRefundReceipt(refundReceipt)
      } else {
        ReceiptUtils.printReceipt(refundReceipt)
      }
      handleRefundActionComplete('print_customer_receipt')
    } else {
      toast.error('Receipt not available for printing')
    }
  }

  // Check if all required refund actions are completed
  const areAllRefundActionsComplete = (payment: Payment) => {
    const requiredActions: string[] = []
    if (payment.ask_merchant_signature === true) requiredActions.push('merchant_signature')
    if (payment.ask_customer_signature === true) requiredActions.push('customer_signature')
    if (payment.ask_customer_identification === true) requiredActions.push('customer_identification')
    if (payment.print_customer_receipt === true) requiredActions.push('print_customer_receipt')
    
    return requiredActions.every(action => completedRefundActions.has(action))
  }

  // Handle closing refund actions and showing receipt
  const handleRefundActionsComplete = async () => {
    if (activeRefundPayment && areAllRefundActionsComplete(activeRefundPayment)) {
      // Refunds are customer-only, always use CUSTOMER type
      setRefundReceiptPaymentId(activeRefundPayment.id)
      setRefundReceiptType('CUSTOMER')
      setIsRefundReceiptOpen(true)
      setActiveRefundPayment(null)
      setCompletedRefundActions(new Set())
      setRefundReceipt(null)
      // Refetch order data to update order status after completing all actions
      await refetch()
    }
  }

  const handleFullRefund = async () => {
    if (!order || !id) return

    setIsProcessingRefund(true)
    setError(null)

    try {
      // Find the original card payment to get terminal data
      const originalPayment = order.payments?.find(p => 
        p.payment_method === 'CARD' && 
        p.status === 'COMPLETED' && 
        p.transaction_type !== 'REFUND' && 
        p.transaction_type !== 'PARTIAL_REFUND'
      )

      const paymentConfig = buildPaymentConfig(originalPayment)

      const result = await refundOrderFull({
        variables: { 
          order_id: parseInt(id),
          payment_config: paymentConfig
        }
      })

      const response = result.data?.refundOrderFull

      if (response?.success) {
        const refundPayment = response.payment as Payment | undefined

        if (refundPayment) {
          // For all refunds, verify completion via polling against the database
          // Don't set activeRefundPayment here - wait for polling to confirm completion
          toast.success(response.message || 'Refund initiated. Waiting for confirmation...')
          startRefundPolling(Number(refundPayment.id))
        } else {
          // Fallback if no payment object is returned
          toast.success(response.message || 'Full refund processed successfully')
          await refetch()
        }
      } else {
        throw new Error(response?.message || 'Failed to process refund')
      }
    } catch (error: any) {
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'Failed to process refund'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setIsProcessingRefund(false)
    }
  }

  const handlePartialRefund = async (amountCents: number) => {
    if (!order || !id) return

    setIsProcessingRefund(true)
    setError(null)

    try {
      // Find the original card payment to get terminal data
      const originalPayment = order.payments?.find(p => 
        p.payment_method === 'CARD' && 
        p.status === 'COMPLETED' && 
        p.transaction_type !== 'REFUND' && 
        p.transaction_type !== 'PARTIAL_REFUND'
      )

      const paymentConfig = buildPaymentConfig(originalPayment)

      const result = await refundOrderPartial({
        variables: {
          order_id: parseInt(id),
          amount_cents: amountCents,
          payment_config: paymentConfig
        }
      })

      const response = result.data?.refundOrderPartial

      if (response?.success) {
        const refundPayment = response.payment as Payment | undefined

        if (refundPayment) {
          // For all refunds, verify completion via polling against the database
          // Don't set activeRefundPayment here - wait for polling to confirm completion
          toast.success(response.message || 'Refund initiated. Waiting for confirmation...')
          startRefundPolling(Number(refundPayment.id))
        } else {
          toast.success(response.message || 'Partial refund processed successfully')
          await refetch()
        }
      } else {
        throw new Error(response?.message || 'Failed to process refund')
      }
    } catch (error: any) {
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'Failed to process refund'
      setError(errorMessage)
      toast.error(errorMessage)
      throw error // Re-throw so RefundModal can handle it
    } finally {
      setIsProcessingRefund(false)
    }
  }

  // WEBSHOP / HUBRISE money never moved through our terminal: the customer is refunded at Mollie
  // or by the platform, and the POS only RECORDS the amount (recordExternalRefund, API 2026-10-09).
  // No terminal polling — the API writes the completed row at once. Refusals come back verbatim
  // ("External refund refused: …") and the modal shows them inline.
  const handleRecordExternalRefund = async (amountCents: number) => {
    if (!order || !id) return

    setIsRecordingExternalRefund(true)
    setError(null)

    try {
      const result = await recordExternalRefund({
        variables: { order_id: parseInt(id), amount_cents: amountCents }
      })
      const response = result.data?.recordExternalRefund
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to record the refund')
      }
      toast.success(response.message || 'External refund recorded')
    } catch (error: any) {
      // Not a page error: the modal is still open and shows the refusal where the amount was typed.
      const errorMessage = error.graphQLErrors?.[0]?.message || error.message || 'Failed to record the refund'
      throw new Error(errorMessage)
    } finally {
      setIsRecordingExternalRefund(false)
    }

    // The row is written. Reload outside the try: a failed reload must never read as a failed
    // refund — the modal would invite a retry and the API allows several partials.
    refetch().catch(() => {
      toast.error('Refund recorded, but the order could not be reloaded. Refresh the page.')
    })
  }

  // Which refund path an order takes. POS / KIOSK sales are refunded on the terminal below;
  // WEBSHOP / HUBRISE refunds are recorded (plan 0n, owner 2026-10-12).
  const isExternallyRefundedChannel = (order: Order) =>
    order.order_source === 'WEBSHOP' || order.order_source === 'HUBRISE'

  // Mirrors the API's eligibility only: a completed sale, not unpaid / refunded / handled
  // externally, something still paid. Permission is checked apart, so the no-permission copy
  // appears only when an Owner actually could record one.
  const isExternalRefundEligible = (order: Order) => {
    if (!isExternallyRefundedChannel(order) || order.externally_handled) return false
    if (order.status === 'UNPAID' || order.status === 'REFUNDED') return false
    const hasCompletedSale = order.payments?.some(p => p.status === 'COMPLETED' && p.transaction_type === 'SALE')
    if (!hasCompletedSale) return false
    return externalRefundTotals(order).remainingCents > 0
  }

  const canRecordExternal = (order: Order) => isExternalRefundEligible(order) && canRecordExternalRefund()

  // Check if refunds are possible
  const canRefund = (order: Order) => {
    // Cannot refund unpaid orders
    if (order.status === 'UNPAID') return false
    
    // Check if order has completed payments
    const hasCompletedPayments = order.payments?.some(p => p.status === 'COMPLETED')
    if (!hasCompletedPayments) return false

    // Check if already fully refunded
    if (order.status === 'REFUNDED') return false

    // Check if there's already a partial refund
    const hasPartialRefund = order.payments?.some(p => p.transaction_type === 'PARTIAL_REFUND')
    if (hasPartialRefund) {
      // Can still do full refund if partial refund exists (but API will prevent this)
      return false
    }

    return true
  }

  const canFullRefund = (order: Order) => {
    if (!canRefund(order)) return false
    // Externally-handled HubRise orders must be refunded via the platform.
    if (order.externally_handled) return false

    // Full refunds only on CANCELLED or COMPLETED orders
    if (order.status !== 'CANCELLED' && order.status !== 'COMPLETED') return false

    // Check if there's already a partial refund
    const hasPartialRefund = order.payments?.some(p => p.transaction_type === 'PARTIAL_REFUND')
    if (hasPartialRefund) return false

    // Check permissions for full order total
    return canProcessRefund(order.total_cents, order.created_at)
  }

  const canPartialRefund = (order: Order) => {
    if (!canRefund(order)) return false
    // Externally-handled HubRise orders must be refunded via the platform.
    if (order.externally_handled) return false

    // Check if there's already a partial refund (only one allowed)
    const hasPartialRefund = order.payments?.some(
      p => p.transaction_type === 'PARTIAL_REFUND'
    )
    if (hasPartialRefund) return false

    // Check permissions for max partial refund (50% of order)
    const maxPartialRefund = Math.floor(order.total_cents * 0.5)
    return canProcessRefund(maxPartialRefund, order.created_at)
  }

  // Runtime fallbacks stay: the API may serve a status this build predates.
  const getStatusColor = (status: OrderStatus) =>
    STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-800'

  const getStatusIcon = (status: OrderStatus) => {
    const Icon = STATUS_ICONS[status] ?? ClockIcon
    return <Icon className="h-5 w-5" />
  }

  // Takes the order, not just its status: the own-delivery rules below cannot be
  // expressed from a status alone.
  const getAvailableStatusTransitions = (order: Order): OrderStatus[] => {
    const transitions: OrderStatus[] = []
    const ownDelivery = isOwnDelivery(order)

    switch (order.status) {
      case 'UNPAID':
        if (canCancelOrders()) {
          transitions.push('CANCELLED')
        }
        break
      case 'SCHEDULED':
        // Cancel only. Manual SCHEDULED → CONFIRMED is rejected by the API
        // (release is cron-only; pickup times are immutable — cancel + re-place
        // is the one way to change a time). Do not add a release button.
        if (canCancelOrders()) {
          transitions.push('CANCELLED')
        }
        break
      case 'CONFIRMED':
        if (canStartPreparingOrders()) {
          transitions.push('PREPARING')
        }
        if (canCancelOrders()) {
          transitions.push('CANCELLED')
        }
        break
      case 'PREPARING':
        transitions.push('READY')
        if (canCancelOrders()) {
          transitions.push('CANCELLED')
        }
        break
      case 'READY':
        // An own delivery leaves READY only through the driver's claim — the API hard-
        // rejects ready → completed for it (OWN_DELIVERY_COMPLETE_FROM_READY_ERROR), so
        // offering the button here would fail on every tap. Platform deliveries and every
        // pickup order still complete normally.
        if (!ownDelivery) {
          transitions.push('COMPLETED')
        }
        break
      case 'IN_DELIVERY':
        // The one FOH fallback for a driver who forgot to tap "delivered" on /driver
        // (owner decision 2026-08-26: the delivery board has no advance buttons). The
        // driver's claim stamp survives completion. OWN ONLY: a platform order at
        // IN_DELIVERY is with the platform's own courier, and completing it here would
        // sync a false "delivered" back to them while they are still driving.
        if (ownDelivery) {
          transitions.push('COMPLETED')
        }
        break
      case 'COMPLETED':
        break
      case 'CANCELLED':
        break
      default:
        break
    }
    
    return transitions
  }

  const getStatusButtonText = (status: OrderStatus) => {
    switch (status) {
      case 'PREPARING': return 'Start Preparing'
      case 'READY': return 'Mark Ready'
      case 'COMPLETED': return 'Complete Order'
      case 'CANCELLED': return 'Cancel Order'
      default: return status
    }
  }

  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`
  }

  // Tolerant of BOTH shapes on purpose: the Order fields are ISO (API 2026-10-08), but this
  // same formatter also prints payment.created_at / processed_at, and Payment timestamps are
  // still epoch milliseconds on the API (its todo, audit 2026-10-08). Not lib/format.ts.
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '—'
    const ms = Number(dateString)
    const date = isNaN(ms) ? new Date(dateString) : new Date(ms)
    return date.toLocaleString()
  }


  // Organize order lines hierarchically (for meal deals)
  const organizeOrderLines = (lines: OrderLine[]): OrderLine[] => {
    const mainLines = lines.filter(line => !line.parent_order_line_id)
    const organized: OrderLine[] = []
    
    mainLines.forEach(mainLine => {
      organized.push(mainLine)
      // Add child lines if they exist
      if (mainLine.childOrderLines && mainLine.childOrderLines.length > 0) {
        mainLine.childOrderLines.forEach(child => {
          const fullChild = lines.find(l => l.id === child.id)
          if (fullChild) {
            organized.push(fullChild)
          }
        })
      }
    })
    
    return organized
  }

  const getRelationshipLabel = (relationshipType?: string) => {
    switch (relationshipType) {
      case 'MAIN_ITEM': return 'Main Item'
      case 'BUNDLE_UPGRADE': return 'Upgrade'
      case 'INCLUDED_PART': return 'Included'
      case 'CUSTOMIZATION': return 'Customization'
      case 'SIDE_ITEM': return 'Side Item'
      default: return ''
    }
  }

  const parseCustomizations = (line: OrderLine) => {
    const customizations: string[] = []
    
    // 1. New API strategy: meaningful labels and group names
    // This is the preferred method as the API handles configuration changes
    if (line.customizations && Array.isArray(line.customizations) && line.customizations.length > 0) {
      line.customizations.forEach(cust => {
        const priceSuffix = cust.price_delta_cents && cust.price_delta_cents !== 0 
          ? ` (${cust.price_delta_cents > 0 ? '+' : ''}${formatPrice(cust.price_delta_cents)})` 
          : ''
        customizations.push(`${cust.group_name}: ${cust.label}${priceSuffix}`)
      })
      return customizations
    }

    // 2. Fallback: Parse customizations_json (contains IDs and price changes)
    if (line.customizations_json) {
      try {
        const parsed = typeof line.customizations_json === 'string' 
          ? JSON.parse(line.customizations_json) 
          : line.customizations_json
          
        if (parsed.selected_options && Array.isArray(parsed.selected_options)) {
          parsed.selected_options.forEach((opt: any) => {
            const priceSuffix = opt.price_delta_cents && opt.price_delta_cents !== 0 
              ? ` (${opt.price_delta_cents > 0 ? '+' : ''}${formatPrice(opt.price_delta_cents)})` 
              : ''
            // Note: We only have IDs here, so we show a descriptive fallback
            customizations.push(`Option #${opt.option_item_id}${priceSuffix}`)
          })
        }
        
        if (parsed.bundle_selections && Array.isArray(parsed.bundle_selections)) {
          parsed.bundle_selections.forEach((sel: any) => {
            const priceSuffix = sel.price_delta_cents && sel.price_delta_cents !== 0 
              ? ` (${sel.price_delta_cents > 0 ? '+' : ''}${formatPrice(sel.price_delta_cents)})` 
              : ''
            customizations.push(`Selection #${sel.selected_variant_id}${priceSuffix}`)
          })
        }
      } catch (e) {
      }
    }
    
    return customizations
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (queryError || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-4">
            {queryError?.message || 'The order you are looking for does not exist or you do not have permission to view it.'}
          </p>
          <Link
            to="/orders"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Orders
          </Link>
        </div>
      </div>
    )
  }

  const availableTransitions = getAvailableStatusTransitions(order)
  const organizedLines = organizeOrderLines(order.orderLines)
  const totalTax = order.orderLines.reduce((sum, line) => sum + line.tax_amount_cents, 0)
  // "Street 12" / "1234 AB City" — each dropped when empty so a partial address never
  // renders a blank line or a stray separator.
  const deliveryAddressLines = [
    [order.delivery_street, order.delivery_house_number].filter(Boolean).join(' '),
    [order.delivery_postcode, order.delivery_city].filter(Boolean).join(' '),
  ].filter(Boolean)
  // Calculate total paid excluding refunds (refunds should not be added to paid amount)
  const totalPaid = order.payments?.filter(p => 
    p.status === 'COMPLETED' && 
    p.transaction_type !== 'REFUND' && 
    p.transaction_type !== 'PARTIAL_REFUND'
  ).reduce((sum, p) => sum + p.amount_cents, 0) || 0
  const hasPayments = order.payments && order.payments.length > 0
  const hasCompletedPayments = order.payments?.some(p => p.status === 'COMPLETED') || false
  // Check if any payment requires customer signature or identification (for merchant receipt)
  const hasMerchantReceiptAvailable = order.payments?.some(p =>
    p.status === 'COMPLETED' && (p.ask_customer_signature === true || p.ask_customer_identification === true)
  ) || false
  // Bezorgbon: same rule as the delivery board (own delivery + a street), never a
  // platform-courier order. Deliberately NOT gated on payment or status: the slip is
  // money-free and the API prints "!!! NIET BETAALD !!!" itself, and this page is where a
  // completed order gets looked up for a reprint.
  const hasDeliverySlipAvailable = canPrintDeliverySlip(order)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/orders"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-semibold text-gray-900">
                Order #{order.order_number}
              </h1>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                {getStatusIcon(order.status)}
                <span className="ml-2">{order.status}</span>
              </span>
              {order.is_preorder && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-cyan-100 text-cyan-800">
                  <ClockIcon className="h-5 w-5" />
                  <span className="ml-2">Pre-order · {formatDate(order.scheduled_for)}</span>
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Created {formatDate(order.created_at)}
              {order.current_status_since && ` • Last updated ${formatDate(order.current_status_since)}`}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          {(hasCompletedPayments || hasDeliverySlipAvailable) && (
            <div className="relative">
              <button
                onClick={() => setIsReceiptMenuOpen((open) => !open)}
                disabled={isCreatingReceipt}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreatingReceipt ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Loading receipts...</span>
                  </>
                ) : (
                  <>
                    <DocumentTextIcon className="h-4 w-4" />
                    <span>Receipts</span>
                  </>
                )}
              </button>

              {isReceiptMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                  <div className="py-1">
                    {hasCompletedPayments && (
                      <button
                        onClick={async () => {
                          setIsReceiptMenuOpen(false)
                          await handleCreateReceipt('CUSTOMER')
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Customer receipt
                      </button>
                    )}
                    {hasMerchantReceiptAvailable && (
                      <button
                        onClick={async () => {
                          setIsReceiptMenuOpen(false)
                          await handleCreateReceipt('MERCHANT')
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Merchant receipt
                      </button>
                    )}
                    {hasDeliverySlipAvailable && (
                      <button
                        onClick={() => {
                          setIsReceiptMenuOpen(false)
                          openDeliverySlips([order])
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        Bezorgbon
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Incoming HubRise order — needs handling in OrderLine */}
      {order.status === 'RECEIVED' && canHandleIncoming && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-4 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-base font-semibold text-amber-900">
                This order needs to be handled in OrderLine
              </h3>
              <p className="mt-1 text-sm text-amber-900">
                We couldn't fully ingest this HubRise order. Prepare it from the OrderLine app — look for{' '}
                <span className="font-semibold">{order.customer_name || '(no customer name)'}</span>,
                print the receipt for the kitchen, then mark it completed here.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setIsHandleExternallyOpen(true)}
              className="shrink-0 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Handle in OrderLine
            </button>
          </div>
        </div>
      )}

      <HandleExternallyModal
        isOpen={isHandleExternallyOpen}
        orderId={order.id}
        orderNumber={order.order_number}
        customerName={order.customer_name}
        onClose={() => setIsHandleExternallyOpen(false)}
        onCompleted={() => {
          refetch()
        }}
      />

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg">
          <div className="flex items-center justify-between">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="ml-4 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Information */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Order Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Customer</label>
                <div className="mt-1 flex items-center space-x-2">
                  <UserIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{order.customer_name || 'Walk-in'}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Order Type</label>
                <div className="mt-1 flex items-center space-x-2">
                  <ShoppingBagIcon className="h-4 w-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{order.order_type.replace('_', ' ')}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Staff Member</label>
                <p className="mt-1 text-sm text-gray-900">{order.staffUser.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Order Date</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(order.created_at)}</p>
              </div>
              {order.is_preorder && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">Scheduled for</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(order.scheduled_for)}</p>
                </div>
              )}
            </div>
            {/* Delivery block. Gated on delivered_by === 'own', NOT on order_type: a
                platform order is also order_type DELIVERY but their courier drives it and
                the address fields are always empty, so this would render a blank card.
                Never gate on delivery_fee_cents either — a first-party order over the
                zone's free-delivery threshold has no fee but still needs an address. */}
            {order.delivered_by === 'own' && (deliveryAddressLines.length > 0 || order.customer_phone) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-500 mb-2">Delivery</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md space-y-1">
                  {deliveryAddressLines.map((line, i) => (
                    <p key={i} className={i === 0 ? 'font-medium' : ''}>{line}</p>
                  ))}
                  {order.customer_phone && (
                    <p>
                      <a href={`tel:${order.customer_phone}`} className="text-primary-700 hover:underline">
                        {order.customer_phone}
                      </a>
                      {/* Uber Eats relay number: the caller keys this code in after the prompt. */}
                      {order.customer_phone_access_code && (
                        <span className="text-gray-700"> · code {order.customer_phone_access_code}</span>
                      )}
                    </p>
                  )}
                  {order.delivery_instructions && (
                    <p className="mt-2 text-blue-800 font-medium">{order.delivery_instructions}</p>
                  )}
                </div>
              </div>
            )}
            {/* Delivery route: what the customer sees on the tracking page, for staff taking
                a "where is my food" call. Null estimate = no estimate, never "late". */}
            {routeRun && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-500 mb-2">Delivery route</label>
                <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md grid grid-cols-2 gap-x-4 gap-y-3">
                  <div>
                    <p className="text-xs text-gray-500">Route</p>
                    <p className="font-medium">
                      Route {routeRun.id} ·{' '}
                      {routeRun.status === 'PLANNED'
                        ? 'Planned'
                        : routeRun.status === 'ACTIVE'
                          ? 'On the road'
                          : 'Completed'}
                    </p>
                  </div>
                  {routeStop && (
                    <div>
                      <p className="text-xs text-gray-500">Stop</p>
                      <p className="font-medium">
                        Stop {routeStop.number} of {routeStop.total}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500">Driver</p>
                    <p className="font-medium">{routeRun.driver?.name ?? 'Not assigned yet'}</p>
                  </div>
                  {routeRun.started_at && (
                    <div>
                      <p className="text-xs text-gray-500">Departed</p>
                      <p className="font-medium">{formatClockTime(routeRun.started_at)}</p>
                    </div>
                  )}
                  {routeRun.status === 'ACTIVE' && order.status === 'IN_DELIVERY' && (
                    <div>
                      <p className="text-xs text-gray-500">Expected arrival</p>
                      <p className="font-medium">{formatEta(routeInfo?.delivery_eta_at) ?? 'No estimate yet'}</p>
                    </div>
                  )}
                  {routeInfo?.delivery_stops_before != null && (
                    <div>
                      <p className="text-xs text-gray-500">Stops before this one</p>
                      <p className="font-medium">
                        {routeInfo.delivery_stops_before === 0 ? 'Next stop' : routeInfo.delivery_stops_before}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
            {order.notes && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-500 mb-2">Notes</label>
                <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">{order.notes}</p>
              </div>
            )}
          </div>

          {/* Order Items */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Order Items</h2>
            <div className="space-y-4">
              {organizedLines.map((line) => {
                const isChild = !!line.parent_order_line_id
                const customizations = parseCustomizations(line)
                
                return (
                  <div
                    key={line.id}
                    className={`border border-gray-200 rounded-lg p-4 ${isChild ? 'ml-6 bg-gray-50' : ''}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {line.quantity}x {line.productVariant?.product.name} - {line.productVariant?.name}
                          </span>
                          {line.relationship_type && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                              {getRelationshipLabel(line.relationship_type)}
                            </span>
                          )}
                        </div>
                        {customizations.length > 0 && (
                          <div className="mt-2 text-xs text-gray-600">
                            <span className="font-medium">Customizations:</span>
                            <ul className="list-disc list-inside ml-2 mt-1">
                              {customizations.map((cust, idx) => (
                                <li key={idx}>{cust}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {line.notes && (
                          <p className="mt-2 text-xs text-gray-600 italic">Note: {line.notes}</p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-sm font-medium text-gray-900">
                          {formatPrice(line.total_cents)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatPrice(line.unit_price_cents)} each
                        </p>
                        {canViewMargins() && line.relationship_type !== 'BUNDLE_UPGRADE' && (
                          <>
                            {line.cogs_cents != null && (
                              <p className="text-xs text-gray-400 mt-0.5">
                                Cost: {formatPrice(line.cogs_cents)}
                              </p>
                            )}
                            {line.margin_cents != null && (
                              <p className="text-xs text-emerald-600 mt-0.5">
                                Margin: {formatPrice(line.margin_cents)} ({line.margin_percentage}%)
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Payment Information */}
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Payment Information</h2>
            {hasPayments ? (
              <div className="space-y-4">
                {order.payments!.map((payment) => (
                  <div key={payment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Payment Method</label>
                        <p className="mt-1 text-sm text-gray-900">{payment.payment_method}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Status</label>
                        <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          payment.status === 'UNKNOWN' ? 'bg-blue-100 text-blue-800' :
                          payment.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                          payment.status === 'CANCELED' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {payment.status}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Amount</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">{formatPrice(payment.amount_cents)}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-500">Date</label>
                        <p className="mt-1 text-sm text-gray-900">{formatDate(payment.created_at)}</p>
                      </div>
                      {payment.transaction_id && (
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-500">Transaction ID</label>
                          <p className="mt-1 text-sm text-gray-900 font-mono">{payment.transaction_id}</p>
                        </div>
                      )}
                      {payment.processed_at && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Processed At</label>
                          <p className="mt-1 text-sm text-gray-900">{formatDate(payment.processed_at)}</p>
                        </div>
                      )}
                      {payment.transaction_type && (
                        <div>
                          <label className="block text-sm font-medium text-gray-500">Transaction Type</label>
                          <span className={`mt-1 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            payment.transaction_type === 'REFUND' || payment.transaction_type === 'PARTIAL_REFUND'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {payment.transaction_type === 'PARTIAL_REFUND' ? 'PARTIAL REFUND' : payment.transaction_type}
                          </span>
                          {order && isExternallyRecordedRefund(payment) && (
                            <p className="mt-1 text-xs text-gray-500">
                              Recorded externally — refunded by {refundProviderLabel(payment.provider, order)}
                            </p>
                          )}
                        </div>
                      )}
                      {/* Refund receipt actions for refund payments */}
                      {payment.status === 'COMPLETED' &&
                        (payment.transaction_type === 'REFUND' ||
                          payment.transaction_type === 'PARTIAL_REFUND') && (
                          <div className="md:col-span-2 mt-2 flex flex-wrap gap-2">
                            <button
                              onClick={() => {
                                setRefundReceiptPaymentId(Number(payment.id))
                                setRefundReceiptType('CUSTOMER')
                                setIsRefundReceiptOpen(true)
                              }}
                              className="inline-flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                            >
                              <DocumentTextIcon className="h-4 w-4" />
                              <span>View Customer Receipt</span>
                            </button>
                            {/* Note: Refunds are customer-only, merchant receipts are not supported for refunds */}
                          </div>
                        )}
                    </div>
                    {/* Refund Actions */}
                    {payment.status === 'COMPLETED' &&
                     payment.transaction_type !== 'REFUND' &&
                     payment.transaction_type !== 'PARTIAL_REFUND' &&
                     order && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {order.externally_handled ? (
                          <div className="text-sm text-gray-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                            This order was handled externally via HubRise. Refunds must be
                            processed in the HubRise dashboard, not from the POS.
                          </div>
                        ) : isExternallyRefundedChannel(order) ? (
                          canRecordExternal(order) ? (
                            <button
                              onClick={() => setIsExternalRefundOpen(true)}
                              disabled={isRecordingExternalRefund}
                              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              <span>Record external refund</span>
                            </button>
                          ) : isExternalRefundEligible(order) ? (
                            <p className="text-sm text-gray-500">
                              Refunds for this order are made {refundedWhere(order)} and recorded here by an Owner.
                            </p>
                          ) : null
                        ) : (
                        <div className="flex flex-wrap gap-2">
                          {canFullRefund(order) && (
                            <button
                              onClick={() => {
                                setRefundMode('FULL')
                                setRefundFixedAmountCents(order.total_cents)
                                setIsRefundModalOpen(true)
                              }}
                              disabled={isProcessingRefund || isRefundVerifying}
                              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              <span>Full Refund</span>
                            </button>
                          )}
                          {canPartialRefund(order) && (
                            <button
                              onClick={() => {
                                setRefundMode('PARTIAL')
                                setRefundFixedAmountCents(null)
                                setIsRefundModalOpen(true)
                              }}
                              disabled={isProcessingRefund || isRefundVerifying}
                              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              <span>Partial Refund</span>
                            </button>
                          )}
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <CurrencyEuroIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No Payment Yet</h3>
                <p className="mt-1 text-sm text-gray-500">This order has not been paid.</p>
              </div>
            )}
          </div>

          {/* Order History (if current_status_since exists) */}
          {order.current_status_since && (
            <div className="card">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Order History</h2>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Order Created</p>
                    <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                  </div>
                </div>
                {order.current_status_since && (
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">Status Changed to {order.status}</p>
                      <p className="text-xs text-gray-500">{formatDate(order.current_status_since)}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Order Summary</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Items</span>
                <span className="text-sm font-medium text-gray-900">
                  {order.orderLines.reduce((sum, line) => sum + line.quantity, 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Subtotal</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatPrice(order.subtotal_cents)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Tax</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatPrice(totalTax)}
                </span>
              </div>
              {/* GROSS fee (incl. VAT) — since the API's gross receipt contract
                  (2026-08-10) this is the SAME figure the receipt shows. The screens still
                  decompose differently and both close: here Subtotal is goods EX-VAT and
                  Tax sums order LINES only, so the fee's VAT is in neither and the gross
                  fee is the exact remainder to Total. The receipt is all-gross — Subtotal
                  + fee == Total, VAT only disclosed as "waarvan BTW". Don't "align" this
                  block's Subtotal/Tax rows to the receipt's — that breaks the sum here. */}
              {Boolean(order.delivery_fee_cents) && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Delivery fee</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatPrice(order.delivery_fee_cents as number)}
                  </span>
                </div>
              )}
              {/* Statiegeld — carries no BTW, so it is absent from both Subtotal
                  (goods ex-VAT) and Tax (order LINES only) and is the exact
                  remainder to Total, exactly like the delivery fee above. Shown
                  as its own row because it is a deposit passing through us, not
                  a sale — the reports exclude it from revenue for the same
                  reason. Hidden entirely on orders without one. */}
              {Boolean(order.deposit_total_cents) && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    Statiegeld <span className="text-xs text-gray-400">(no BTW)</span>
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatPrice(order.deposit_total_cents as number)}
                  </span>
                </div>
              )}
              {/* Verpakkingskosten — GROSS, and treated exactly like the fee
                  above: Subtotal here is goods EX-VAT and Tax sums order LINES
                  only, so this fee's VAT is in neither and its gross amount is
                  the exact remainder to Total. Absent on dine-in. */}
              {Boolean(order.packaging_fee_cents) && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">Verpakkingskosten</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatPrice(order.packaging_fee_cents as number)}
                  </span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">Total</span>
                  <span className="text-sm font-bold text-gray-900">
                    {formatPrice(order.total_cents)}
                  </span>
                </div>
              </div>
              {canViewMargins() && order.margin_cents != null && (
                <div className="border-t border-gray-200 pt-3 space-y-2">
                  {order.cogs_total_cents != null && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">COGS</span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatPrice(order.cogs_total_cents)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    {/* Goods + verpakkingskosten − COGS − commission; the delivery fee is not in it. */}
                    <span className="text-sm font-medium text-gray-900">Product margin</span>
                    <span className="text-sm font-bold text-emerald-600">
                      {formatPrice(order.margin_cents)} ({order.margin_percentage}%)
                    </span>
                  </div>
                  {order.has_partial_cogs && (
                    <p className="text-xs text-amber-600">
                      Not all items on this order have a recipe — margin shown is an undercount.
                    </p>
                  )}
                </div>
              )}
              {hasPayments && (
                <>
                  <div className="border-t border-gray-200 pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Paid</span>
                      <span className="text-sm font-medium text-green-600">
                        {formatPrice(totalPaid)}
                      </span>
                    </div>
                  </div>
                  {totalPaid < order.total_cents && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Remaining</span>
                      <span className="text-sm font-medium text-orange-600">
                        {formatPrice(order.total_cents - totalPaid)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          {availableTransitions.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              {order.status === 'SCHEDULED' && (
                <p className="text-xs text-gray-500 mb-3">
                  Afhaaltijd staat vast — tijd wijzigen = annuleren (+ terugbetaling) en
                  opnieuw plaatsen. Vrijgeven aan de keuken gebeurt automatisch.
                </p>
              )}
              <div className="space-y-2">
                {availableTransitions.map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    disabled={isUpdating}
                    className={`w-full px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      status === 'CANCELLED'
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isUpdating ? 'Updating...' : getStatusButtonText(status)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Receipt Modal */}
      {isReceiptOpen && (
        <ReceiptModal
          isOpen={isReceiptOpen}
          onClose={() => setIsReceiptOpen(false)}
          orderId={order.id}
          receiptType={receiptType}
        />
      )}

      {/* Refund Receipt Modal */}
      {isRefundReceiptOpen && refundReceiptPaymentId && (
        <ReceiptModal
          isOpen={isRefundReceiptOpen}
          onClose={() => {
            setIsRefundReceiptOpen(false)
            setRefundReceiptPaymentId(null)
          }}
          paymentId={refundReceiptPaymentId}
          receiptType={refundReceiptType}
        />
      )}

      {/* Refund Modal */}
      {isRefundModalOpen && order && (
        <RefundModal
          isOpen={isRefundModalOpen}
          onClose={() => setIsRefundModalOpen(false)}
          onConfirm={refundMode === 'FULL' ? handleFullRefund : handlePartialRefund}
          orderTotalCents={order.total_cents}
          maxRefundCents={Math.floor(order.total_cents * 0.5)}
          isLoading={isProcessingRefund}
          mode={refundMode}
          fixedAmountCents={refundMode === 'FULL' ? (refundFixedAmountCents ?? order.total_cents) : undefined}
        />
      )}

      {/* External Refund Modal — webshop / platform orders, bookkeeping only */}
      {isExternalRefundOpen && order && (
        <ExternalRefundModal
          onClose={() => setIsExternalRefundOpen(false)}
          order={order}
          onConfirm={handleRecordExternalRefund}
          isLoading={isRecordingExternalRefund}
        />
      )}

      {/* Refund Processing Overlay for Card Refunds */}
      {(isRefundVerifying || activeRefundPayment) && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Processing state - only show when verifying and no active payment yet */}
            {isRefundVerifying && !activeRefundPayment && (
              <>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <div>
                    <p className="text-base font-semibold text-gray-900">Processing refund...</p>
                    <p className="text-sm text-gray-600">
                      Please wait while we verify the refund status.
                    </p>
                    {refundPollingAttempts > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Checking refund status... ({refundPollingAttempts}/360)
                      </p>
                    )}
                  </div>
                </div>

                {refundPollingError && (
                  <p className="mt-2 text-sm text-red-600">{refundPollingError}</p>
                )}

                {/* Cancel refund button */}
                <button
                  onClick={handleCancelRefund}
                  disabled={isCancellingRefund}
                  className="mt-4 w-full px-4 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCancellingRefund ? 'Cancelling refund...' : 'Cancel Refund'}
                </button>
              </>
            )}

            {/* Refund completed with required actions */}
            {activeRefundPayment && (
              <>
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Refund Completed</h3>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Amount</span>
                      <span className="font-semibold text-gray-900">
                        {formatPrice(activeRefundPayment.amount_cents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment Method</span>
                      <span className="font-semibold text-gray-900">
                        {activeRefundPayment.payment_method}
                      </span>
                    </div>
                    {activeRefundPayment.transaction_id && (
                      <div className="pt-2 border-t border-gray-200">
                        <span className="text-xs text-gray-500">
                          Transaction ID: {activeRefundPayment.transaction_id}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Required Actions */}
                {(activeRefundPayment.ask_merchant_signature === true ||
                  activeRefundPayment.ask_customer_signature === true ||
                  activeRefundPayment.ask_customer_identification === true ||
                  activeRefundPayment.print_customer_receipt === true) && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-yellow-900 mb-3">Required Actions</h3>
                    <div className="space-y-3">
                      {/* Merchant Signature Required */}
                      {activeRefundPayment.ask_merchant_signature === true && (
                        <div className={`bg-white rounded-lg p-3 border-2 ${
                          completedRefundActions.has('merchant_signature') 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-yellow-300'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                ⚠️ Merchant Signature Required
                              </p>
                              <p className="text-xs text-gray-600">
                                Please sign the receipt and store it.
                              </p>
                              {completedRefundActions.has('merchant_signature') && (
                                <p className="text-xs text-green-700 mt-1 font-medium">✅ Completed</p>
                              )}
                            </div>
                            {!completedRefundActions.has('merchant_signature') && (
                              <button
                                onClick={handleRefundMerchantSignatureComplete}
                                className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                              >
                                Mark Complete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Customer Signature Required */}
                      {activeRefundPayment.ask_customer_signature === true && (
                        <div className={`bg-white rounded-lg p-3 border-2 ${
                          completedRefundActions.has('customer_signature') 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-yellow-300'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                ⚠️ Customer Signature Required
                              </p>
                              <p className="text-xs text-gray-600">
                                Please have the customer sign the receipt and store it.
                              </p>
                              {completedRefundActions.has('customer_signature') && (
                                <p className="text-xs text-green-700 mt-1 font-medium">✅ Completed</p>
                              )}
                            </div>
                            {!completedRefundActions.has('customer_signature') && (
                              <button
                                onClick={handleRefundCustomerSignatureComplete}
                                className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                              >
                                Mark Complete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Customer Identification Required */}
                      {activeRefundPayment.ask_customer_identification === true && (
                        <div className={`bg-white rounded-lg p-3 border-2 ${
                          completedRefundActions.has('customer_identification') 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-yellow-300'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                ⚠️ Customer Identification Required
                              </p>
                              <p className="text-xs text-gray-600">
                                Please check the customer's ID and write the passport number or driver's license number on the receipt by hand and store it.
                              </p>
                              {completedRefundActions.has('customer_identification') && (
                                <p className="text-xs text-green-700 mt-1 font-medium">✅ Completed</p>
                              )}
                            </div>
                            {!completedRefundActions.has('customer_identification') && (
                              <button
                                onClick={handleRefundCustomerIDComplete}
                                className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                              >
                                Mark Complete
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Print Customer Receipt Required */}
                      {activeRefundPayment.print_customer_receipt === true && (
                        <div className={`bg-white rounded-lg p-3 border-2 ${
                          completedRefundActions.has('print_customer_receipt') 
                            ? 'border-green-300 bg-green-50' 
                            : 'border-yellow-300'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                ⚠️ Print Customer Receipt Required
                              </p>
                              <p className="text-xs text-gray-600">
                                Printing a receipt for the customer is mandatory.
                              </p>
                              {completedRefundActions.has('print_customer_receipt') && (
                                <p className="text-xs text-green-700 mt-1 font-medium">✅ Receipt Printed</p>
                              )}
                              {!refundReceipt && !completedRefundActions.has('print_customer_receipt') && (
                                <p className="text-xs text-yellow-700 mt-1">Loading receipt...</p>
                              )}
                            </div>
                            {!completedRefundActions.has('print_customer_receipt') && refundReceipt && (
                              <button
                                onClick={handleRefundReceiptPrintComplete}
                                className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                              >
                                Print Receipt
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Complete button - only show when all actions are complete */}
                {areAllRefundActionsComplete(activeRefundPayment) && (
                  <button
                    onClick={handleRefundActionsComplete}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    View Refund Receipt
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderDetailPage

